import { useQuery } from '@tanstack/react-query';
import { Flex, Text } from '@chakra-ui/react';
import { _js } from '@ifixit/localize';
import { LatestOrder } from './latest_order';
import { OrdersTable } from './orders_table';
import { OrderType, TrackingType } from './orders_types';
import type { ShopifyStorefront } from 'Shared/shopify/storefront';
import {
   getCustomerOrdersFromAllStores,
   getLocalizedFulfillmentStatus,
} from 'Shared/shopify_orders';
import type { ShopifyOrder } from 'Shared/shopify_orders';

type ServerSideData = {
   orders: OrderType[];
   latestOrder: OrderType | null;
   shopifyStorefronts: ShopifyStorefront[] | null;
   storeName: string;
   isUS: boolean;
};

export function OrderHistory({ data }: { data: ServerSideData }) {
   const ordersQuery = useQuery({
      queryKey: ['orders', data.shopifyStorefronts?.map(sf => sf.storeDomain)],
      queryFn: () =>
         data.shopifyStorefronts
            ? getCustomerOrdersFromAllStores(data.shopifyStorefronts).then(formatOrderData)
            : [],
      enabled: !data.isUS,
      retry: false,
   });
   const orders: OrderType[] = data.isUS ? data.orders : ordersQuery.data || [];
   const latestOrder: OrderType | null = data.isUS ? data.latestOrder : orders?.[0];
   return ordersQuery.isError ? (
      <ErrorMessage />
   ) : ordersQuery.isLoading && !data.isUS ? (
      <LoadingMessage />
   ) : orders && orders.length > 0 ? (
      <OrdersDisplay orders={orders} latestOrder={latestOrder} fromShopify={!data.isUS} />
   ) : (
      <NoOrdersMessage storeName={data.storeName} />
   );
}

function formatOrderData(orders: ShopifyOrder[]) {
   return orders.map((order: ShopifyOrder) => {
      const trackingInfo: TrackingType[] = [];
      order.successfulFulfillments.forEach(fulfillment => {
         fulfillment.trackingInfo.forEach(tracking => {
            trackingInfo.push({
               carrier: fulfillment.trackingCompany,
               number: tracking.number,
               link: tracking.url,
            });
         });
      });
      const [stepNumber, widthPercent] = getShopifyOrderStep(order);
      return {
         orderid: order.orderNumber,
         currentStep: stepNumber,
         invoice_link: order.statusUrl,
         invoiceLink: order.statusUrl,
         order_name: order.name,
         status: getDetailedStatus(order),
         orderStatus: getDetailedStatus(order),
         widthPercent: widthPercent,
         editable: false,
         cancel_order_link: undefined,
         init_date: Date.parse(order.processedAt) / 1000,
         total: order.totalPriceV2.amount,
         currency_type: order.totalPriceV2.currencyCode,
         shipment_tracking: trackingInfo,
         showTracking: order.successfulFulfillments.length ? true : false,
      };
   });
}

const OrdersDisplay = ({
   orders,
   latestOrder,
   fromShopify,
}: {
   orders: OrderType[];
   latestOrder: OrderType | null;
   fromShopify: boolean;
}) => (
   <Flex direction="column" flex="1">
      {latestOrder && <LatestOrder order={latestOrder} />}
      <OrdersTable orders={orders} fromShopify={fromShopify} />
   </Flex>
);

const NoOrdersMessage = ({ storeName }: { storeName: string }) => (
   <Text>{_js('You have no orders with iFixit %1', storeName)}</Text>
);

const ErrorMessage = () => (
   <Text>
      {_js("We're having trouble getting your orders. Please try again later or contact support")}
   </Text>
);

const LoadingMessage = () => <Text>{_js('Loading...')}</Text>;

const step_2 = new Set([
   'UNFULFILLED',
   'RESTOCKED',
   'OPEN',
   'PENDING_FULFILLMENT',
   'PARTIALLY_FULFILLED',
   'IN_PROGRESS',
   'SCHEDULED',
]);
const step_3 = new Set(['FULFILLED']);

function getShopifyOrderStep(order: ShopifyOrder): number[] {
   if (step_2.has(order.fulfillmentStatus)) {
      return [2, 35];
   } else if (
      step_3.has(order.fulfillmentStatus) &&
      (order.successfulFulfillments?.length == 0 ||
         order.successfulFulfillments[0].trackingCompany == null)
   ) {
      return [3, 70];
   } else if (order.successfulFulfillments?.length > 0) {
      return [4, 100];
   }
   return [1, 0];
}

function getDetailedStatus(order: ShopifyOrder): string {
   if (
      step_3.has(order.fulfillmentStatus) &&
      (order.successfulFulfillments?.length == 0 ||
         order.successfulFulfillments[0].trackingCompany == null)
   ) {
      return _js('Packed');
   } else if (order.successfulFulfillments?.length > 0) {
      return _js('Shipped');
   }
   return getLocalizedFulfillmentStatus(order.fulfillmentStatus);
}
